import { FC, useCallback, useContext } from "react";
import { SportFavoritesContext, PrematchFeedContext } from "@finbackoffice/site-core";
import Loading from "components/base/loading/Loading";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import PrematchTimeFilterTabs from "components/side-bar/common/prematch-time-filter/PrematchTimeFilterTabs";
import Translate from "components/base/translate/Translate";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import { Svg } from "components/base/svg/Svg";
import ActiveLink from "components/base/active-link/ActiveLink";
import classNames from "classnames";
import Button from "components/base/button/Button";
import { useParams, usePathname, useRouter } from "next/navigation";
import { PrematchContext } from "contexts";
import { routeFavorites, routeTopLeagues, routeUpcoming } from "components/side-bar/Sidebar";
import AdvancedSidebarPrematchSport from "./sport/AdvancedSidebarPrematchSport";
import styles from "./advanced-sidebar-prematch.module.sass";

const AdvancedSidebarPrematch: FC = () => {
    const params = useParams();
    const router = useRouter();
    const pathname = usePathname();

    const { prematchFeed, prematchIsLoading } = useContext(PrematchFeedContext);
    const { favPrematchLeaguesGameCount } = useContext(SportFavoritesContext);
    const { isSportSelected } = useContext(PrematchContext);

    const handleFavoriteClick = () => {
        router.push(routeFavorites);
    };

    const renderSport = useCallback(
        (sport: ISportFeedResponse) => {
            const isSelected = params.sport === sport.slug || isSportSelected(sport.id);
            return (
                <AdvancedSidebarPrematchSport
                    key={sport.id}
                    sport={sport}
                    openedByDefault={isSelected}
                    isSelected={isSelected}
                />
            );
        },
        [isSportSelected, params.sport],
    );

    return (
        <>
            <div className={styles.sportsNav}>
                <Button
                    type="custom"
                    disabled={favPrematchLeaguesGameCount === 0}
                    className={classNames(styles.sportsNavItem, {
                        [styles.selected]: pathname?.includes(routeFavorites),
                    })}
                    onClick={handleFavoriteClick}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/favorite-events.svg`}
                            className="svg-icon"
                        />
                        <span>
                            <Translate tid="sport_favorite_events" />
                        </span>
                        <span className={styles.gamesCount}>{favPrematchLeaguesGameCount}</span>
                    </>
                </Button>
                <ActiveLink
                    href={routeTopLeagues}
                    includes={routeTopLeagues}
                    activeClassName={styles.selected}
                    className={styles.sportsNavItem}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/top-leagues.svg`}
                            className="svg-icon"
                        />
                        <span>
                            <Translate
                                tid="prematch_topLeagues"
                                namespace={TranslationScopes.PreMatch}
                            />
                        </span>
                    </>
                </ActiveLink>
                <ActiveLink
                    href={routeUpcoming}
                    includes={routeUpcoming}
                    activeClassName={styles.selected}
                    className={styles.sportsNavItem}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/upcoming-matches.svg`}
                            className="svg-icon"
                        />
                        <span>
                            <Translate
                                tid="prematch_upcoming_matches"
                                namespace={TranslationScopes.PreMatch}
                            />
                        </span>
                    </>
                </ActiveLink>
            </div>
            <PrematchTimeFilterTabs />
            <div className={styles.sportsNav}>
                {!prematchIsLoading ? <>{prematchFeed.map(renderSport)}</> : <Loading />}
            </div>
        </>
    );
};

export default AdvancedSidebarPrematch;
